/**
 * One-line "last checked" note for an approval board (Purge, Deletions):
 * proves the proposing job ran even when it proposed nothing.
 */
import { useEffect, useState } from 'react';

import type { AdminJobRunState } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { formatSummary, jobHealth } from './job-labels.js';

const monoFont = "'JetBrains Mono', monospace";

export function JobLastRunNote({ job_name, refreshKey }: { job_name: string; refreshKey?: unknown }): JSX.Element | null {
  const C = useC();
  const api = useAdminApi();
  const [job, setJob] = useState<AdminJobRunState | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    api
      .listJobs()
      .then((res) => {
        if (live) setJob(res.jobs.find((j) => j.job_name === job_name) ?? null);
      })
      .catch(() => {
        if (live) setJob(undefined);
      });
    return () => {
      live = false;
    };
  }, [api, job_name, refreshKey]);

  if (job === undefined) return null;
  const style = { fontFamily: monoFont, fontSize: 11, letterSpacing: 0.3, color: C.muted } as const;
  if (job === null || job.last_completed_at === null) {
    return <div style={style}>Job has not run yet. It runs within a few minutes of each deploy when due.</div>;
  }
  const health = jobHealth(job);
  const color = health === 'failed' ? C.coralFg : health === 'overdue' ? C.amberFg : C.muted;
  return (
    <div style={{ ...style, color }}>
      Last checked {formatIsoCompact(job.last_completed_at)}
      {health === 'failed' ? ` · failed (${job.last_error_class ?? 'unknown'})` : ''}
      {health === 'overdue' ? ' · overdue' : ''}
      {' · '}
      {formatSummary(job.last_summary)} · next {formatIsoCompact(job.next_due_at)}
    </div>
  );
}
